import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Briefcase,
  ChevronRight,
  ClipboardList,
  Gamepad2,
  Globe2,
  GraduationCap,
  Languages,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MoreVertical,
  Settings,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabaseClient';
import { canUseJobTracker } from '../../lib/jobTracker';
import 'flag-icons/css/flag-icons.min.css';

const EXAM_PATH = '/exam/b2';

const NAV_ITEMS = [
  {
    id: 'startseite',
    label: 'Startseite',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    id: 'chat',
    label: 'Chat',
    path: '/chat',
    icon: MessageCircle,
  },
  {
    id: 'grammar',
    label: 'Grammatikbuch',
    path: '/grammar',
    icon: BookOpen,
  },
  {
    id: 'vocabulary',
    label: 'Wortschatz',
    path: '/vocabulary',
    icon: Languages,
    children: [
      {
        id: 'known-words',
        label: 'Fjalët e Ditura',
        path: '/unknown-words',
      },
    ],
  },
  {
    id: 'exam',
    label: 'Goethe-B2-Prüfung',
    path: EXAM_PATH,
    icon: GraduationCap,
    children: [
      {
        id: 'exam-lesen',
        label: 'Lesen',
        path: '/exam/b2/lesen',
      },
    ],
  },
  {
    id: 'games',
    label: 'Spiele & Praxis',
    path: '/timed-challenge',
    icon: Gamepad2,
    children: [
      {
        id: 'timed-challenge',
        label: 'Zeit-Challenge',
        path: '/timed-challenge',
      },
      {
        id: 'writing-journal',
        label: 'Schreibjournal',
        path: '/writing-journal',
      },
      {
        id: 'typing-practice',
        label: 'Tipptraining',
        path: '/typing-practice',
      },
    ],
  },
  {
    id: 'progress',
    label: 'Fortschritt',
    path: '/progress',
    icon: BarChart3,
  },
  {
    id: 'profession',
    label: 'Beruf',
    path: '/profession',
    icon: Briefcase,
  },
];

const getUserDisplayName = (profile) => (
  [profile?.firstName || profile?.first_name, profile?.lastName || profile?.last_name]
    .filter(Boolean)
    .join(' ')
  || 'Student'
);

const Sidebar = ({ isOpen, onClose, isCollapsed }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const displayName = getUserDisplayName(user);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const accountMenuRef = useRef(null);

  // Track unread chat messages for sidebar badge
  useEffect(() => {
    if (!user?.id) {
      setUnreadChatCount(0);
      return undefined;
    }

    let isMounted = true;

    const fetchUnreadCount = async () => {
      try {
        const { data, error } = await supabase.rpc('unread_chat_message_count');
        if (!error && typeof data === 'number') {
          if (isMounted) setUnreadChatCount(data);
          return;
        }

        // Fallback if RPC is pending migration on Supabase
        const { data: memberships } = await supabase
          .from('conversation_members')
          .select('conversation_id')
          .eq('user_id', user.id);

        const convIds = (memberships || []).map((m) => m.conversation_id);
        if (!convIds.length) {
          if (isMounted) setUnreadChatCount(0);
          return;
        }

        const { data: messages } = await supabase
          .from('messages')
          .select('id')
          .in('conversation_id', convIds)
          .neq('sender_id', user.id)
          .order('timestamp', { ascending: false })
          .limit(100);

        if (!messages || !messages.length) {
          if (isMounted) setUnreadChatCount(0);
          return;
        }

        const msgIds = messages.map((m) => m.id);
        const [readsResult, hidesResult] = await Promise.all([
          supabase
            .from('message_reads')
            .select('message_id')
            .eq('user_id', user.id)
            .in('message_id', msgIds),
          supabase
            .from('message_hides')
            .select('message_id')
            .eq('user_id', user.id)
            .in('message_id', msgIds),
        ]);

        const readSet = new Set((readsResult.data || []).map((r) => r.message_id));
        const hideSet = new Set((hidesResult.data || []).map((h) => h.message_id));
        const unread = messages.filter((m) => !readSet.has(m.id) && !hideSet.has(m.id)).length;

        if (isMounted) setUnreadChatCount(unread);
      } catch (err) {
        console.warn('Unable to load unread chat count:', err);
      }
    };

    fetchUnreadCount();

    const channel = supabase
      .channel(`sidebar-chat-count:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, fetchUnreadCount)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reads' }, fetchUnreadCount)
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [user?.id, location.pathname]);

  // Accordion open/close state
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    NAV_ITEMS.forEach((item) => {
      if (item.children) {
        const matchesChild = item.children.some((c) => location.pathname === c.path || (c.path === '/unknown-words' && location.pathname === '/known-words'));
        const matchesParent = item.path === EXAM_PATH
          ? location.pathname === EXAM_PATH || location.pathname.startsWith(EXAM_PATH + '/')
          : location.pathname === item.path;
        if (matchesChild || matchesParent) {
          initial[item.id] = true;
        }
      }
    });
    return initial;
  });

  // Automatically expand if currently navigating to any of the children
  useEffect(() => {
    NAV_ITEMS.forEach((item) => {
      if (item.children) {
        const matchesChild = item.children.some((c) => location.pathname === c.path || (c.path === '/unknown-words' && location.pathname === '/known-words'));
        const matchesParent = item.path === EXAM_PATH
          ? location.pathname === EXAM_PATH || location.pathname.startsWith(EXAM_PATH + '/')
          : location.pathname === item.path;
        if (matchesChild || matchesParent) {
          setExpanded((prev) => (prev[item.id] ? prev : { ...prev, [item.id]: true }));
        }
      }
    });
  }, [location.pathname]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [accountMenuOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-zinc-950/40 backdrop-blur-sm lg:hidden ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        } transition-opacity duration-300`}
        onClick={onClose}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex transform flex-col h-full bg-white/95 text-sidebar-foreground border-r border-zinc-200/80 backdrop-blur-xl shadow-2xl transition-all duration-300 ease-in-out dark:bg-zinc-900/95 dark:border-white/10 lg:static lg:h-full lg:translate-x-0 lg:bg-transparent lg:border-r-0 lg:shadow-none lg:backdrop-blur-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'lg:w-[72px]' : 'lg:w-[236px]'} w-[236px]`}
      >
        {/* Brand Header */}
        <div className="mx-2 flex h-14 shrink-0 items-center border-b border-zinc-200/60 px-3 dark:border-white/10">
          <Link
            to="/dashboard"
            onClick={onClose}
            className={`flex items-center gap-2 ${isCollapsed ? 'lg:justify-center lg:w-full' : ''}`}
          >
            <img
              src={isCollapsed ? `${process.env.PUBLIC_URL || ''}/favicon.png` : `${process.env.PUBLIC_URL || ''}/logo.png`}
              alt="DeutschLernen"
              className={`${
                isCollapsed
                  ? 'hidden lg:block h-8 w-8 object-contain'
                  : 'h-8 w-auto max-w-[178px] object-contain'
              } min-h-[32px] transition-all duration-200`}
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = 'true';
                  e.currentTarget.src = isCollapsed ? '/favicon.png' : '/logo.png';
                }
              }}
            />
            {isCollapsed && (
              <img
                src={`${process.env.PUBLIC_URL || ''}/logo.png`}
                alt="DeutschLernen"
                className="h-8 w-auto max-w-[178px] min-h-[32px] object-contain lg:hidden"
                onError={(e) => {
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = 'true';
                    e.currentTarget.src = '/logo.png';
                  }
                }}
              />
            )}
          </Link>
        </div>

        {/* Navigation list */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 custom-scrollbar">
          <div className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const hasChildren = Boolean(item.children && item.children.length > 0);
              const isDirectActive = item.path === EXAM_PATH
                ? location.pathname === EXAM_PATH
                : location.pathname === item.path;
              const isMenuExpanded = Boolean(expanded[item.id]);

              return (
                <div key={item.id} className="space-y-0.5">
                  {hasChildren ? (
                    /* Line with '>': The line itself is fully clickable to navigate to the page and open under */
                    <div
                      className={`group flex items-center justify-between rounded-lg transition-all duration-200 outline-hidden ${
                        isDirectActive
                          ? 'bg-zinc-200/80 text-black font-semibold dark:bg-zinc-800 dark:text-white'
                          : 'text-zinc-600 hover:bg-zinc-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-white'
                      } ${isCollapsed ? 'lg:justify-center' : ''}`}
                    >
                      <Link
                        to={item.path}
                        onClick={() => {
                          if (!isMenuExpanded) {
                            setExpanded((prev) => ({ ...prev, [item.id]: true }));
                          }
                          onClose();
                        }}
                        title={isCollapsed ? t(item.label) : undefined}
                        className={`flex min-w-0 flex-1 items-center gap-3 px-2.5 py-2 text-sm ${
                          isDirectActive ? 'font-semibold text-black dark:text-white' : 'font-medium'
                        } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                      >
                        <Icon
                          size={18}
                          strokeWidth={isDirectActive ? 2.3 : 1.9}
                          className={`shrink-0 transition-colors ${
                            isDirectActive
                              ? 'text-black dark:text-white'
                              : 'text-zinc-500 group-hover:text-black dark:text-zinc-400 dark:group-hover:text-white'
                          }`}
                        />
                        <span className={`min-w-0 truncate text-left ${isCollapsed ? 'lg:hidden' : ''}`}>
                          {t(item.label)}
                        </span>
                      </Link>

                      {/* Chevron button: toggles open/close under without navigating */}
                      {!isCollapsed && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpanded(item.id);
                          }}
                          aria-label={`Toggle ${t(item.label)}`}
                          className={`mr-1 rounded p-1 transition-colors ${
                            isDirectActive
                              ? 'text-black hover:bg-zinc-300/50 dark:text-white dark:hover:bg-zinc-700'
                              : 'text-zinc-400 hover:bg-zinc-200/60 hover:text-black dark:hover:bg-zinc-800'
                          }`}
                        >
                          <ChevronRight
                            size={15}
                            className={`transition-transform duration-200 ${
                              isMenuExpanded ? 'rotate-90' : ''
                            } ${isDirectActive ? 'text-black dark:text-white' : ''}`}
                          />
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Simple link without subpages */
                    <Link
                      to={item.path}
                      onClick={onClose}
                      title={isCollapsed ? t(item.label) : undefined}
                      className={`group flex items-center justify-between rounded-lg px-2.5 py-2 text-sm transition-all duration-200 outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring ${
                        isDirectActive
                          ? 'bg-zinc-200/80 text-black font-semibold dark:bg-zinc-800 dark:text-white'
                          : 'text-zinc-600 hover:bg-zinc-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-white'
                      } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="relative shrink-0 flex items-center justify-center">
                          <Icon
                            size={18}
                            strokeWidth={isDirectActive ? 2.3 : 1.9}
                            className={`shrink-0 transition-colors ${
                              isDirectActive
                                ? 'text-black dark:text-white'
                                : 'text-zinc-500 group-hover:text-black dark:text-zinc-400 dark:group-hover:text-white'
                            }`}
                          />
                          {item.id === 'chat' && unreadChatCount > 0 && isCollapsed && (
                            <span className="absolute -top-1.5 -right-2 hidden lg:flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#DD0000] px-1 text-[9px] font-bold text-white ring-2 ring-white dark:ring-zinc-900 shadow-xs">
                              {unreadChatCount > 9 ? '9+' : unreadChatCount}
                            </span>
                          )}
                        </div>
                        <span className={`min-w-0 truncate ${isDirectActive ? 'font-semibold text-black dark:text-white' : 'font-medium'} ${isCollapsed ? 'lg:hidden' : ''}`}>
                          {t(item.label)}
                        </span>
                      </div>
                      {item.id === 'chat' && unreadChatCount > 0 && (
                        <span className={`ml-2 shrink-0 inline-flex items-center justify-center rounded-full bg-[#DD0000] px-2 py-0.5 text-[11px] font-bold text-white shadow-xs ${isCollapsed ? 'lg:hidden' : ''}`}>
                          {unreadChatCount > 99 ? '99+' : unreadChatCount}
                        </span>
                      )}
                    </Link>
                  )}

                  {/* Under pages (sub-items) — clean text, no icon, improved font size */}
                  {hasChildren && isMenuExpanded && !isCollapsed && (
                    <div className="ml-[19px] mt-0.5 mb-1 space-y-0.5 border-l border-zinc-200 dark:border-zinc-800">
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.path || (child.path === '/unknown-words' && location.pathname === '/known-words');

                        return (
                          <Link
                            key={child.id}
                            to={child.path}
                            onClick={onClose}
                            className={`block py-1.5 pl-3 pr-2 text-[13.5px] leading-snug rounded-md transition-colors duration-150 ${
                              childActive
                                ? 'bg-zinc-200/80 text-black font-semibold dark:bg-zinc-800 dark:text-white -ml-px'
                                : 'text-zinc-600 font-medium hover:bg-zinc-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-white'
                            }`}
                          >
                            <span className="truncate block">{t(child.label)}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Personal Job Application Tracker (only for permitted accounts) */}
            {canUseJobTracker(user) && (
              <div className="pt-1">
                <Link
                  to="/applications"
                  onClick={onClose}
                  title={isCollapsed ? t('Aplikimet për Punë') : undefined}
                  className={`group flex items-center justify-between rounded-lg px-2.5 py-2 text-sm transition-all duration-200 outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring ${
                    location.pathname === '/applications'
                      ? 'bg-zinc-200/80 text-black font-semibold dark:bg-zinc-800 dark:text-white'
                      : 'text-zinc-600 hover:bg-zinc-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-white'
                  } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <ClipboardList
                      size={18}
                      strokeWidth={location.pathname === '/applications' ? 2.3 : 1.9}
                      className={`shrink-0 transition-colors ${
                        location.pathname === '/applications'
                          ? 'text-black dark:text-white'
                          : 'text-zinc-500 group-hover:text-black dark:text-zinc-400 dark:group-hover:text-white'
                      }`}
                    />
                    <span className={`min-w-0 truncate ${location.pathname === '/applications' ? 'font-semibold text-black dark:text-white' : 'font-medium'} ${isCollapsed ? 'lg:hidden' : ''}`}>
                      {t('Aplikimet për Punë')}
                    </span>
                  </div>
                </Link>
              </div>
            )}
          </div>

          {/* Admin panel link */}
          {user?.role === 'admin' && (
            <div className="mt-4 border-t border-sidebar-border pt-3">
              <p
                className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/50 ${
                  isCollapsed ? 'lg:hidden' : ''
                }`}
              >
                {t('Administrimi')}
              </p>
              <Link
                to="/admin"
                onClick={onClose}
                title={isCollapsed ? t('Paneli i Kontrollit') : undefined}
                className={`group flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-all duration-200 outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring ${
                  location.pathname === '/admin'
                    ? 'bg-zinc-200/80 text-black font-semibold dark:bg-zinc-800 dark:text-white'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-black dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-white'
                } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
              >
                <BarChart3
                  size={18}
                  className={`shrink-0 transition-colors ${
                    location.pathname === '/admin' ? 'text-black dark:text-white' : 'text-zinc-500 group-hover:text-black dark:text-zinc-400 dark:group-hover:text-white'
                  }`}
                />
                <span className={`${location.pathname === '/admin' ? 'font-semibold text-black dark:text-white' : 'font-medium'} ${isCollapsed ? 'lg:hidden' : ''}`}>
                  {t('Paneli i Kontrollit')}
                </span>
              </Link>
            </div>
          )}
        </nav>

        {/* User Account / Settings Footer */}
        <div className="relative mx-2 border-t border-zinc-200/60 p-2 dark:border-white/10" ref={accountMenuRef}>
          <button
            type="button"
            onClick={() => setAccountMenuOpen((open) => !open)}
            aria-label={t('Hap menynë e llogarisë')}
            aria-expanded={accountMenuOpen}
            className={`flex w-full items-center gap-2 rounded-xl p-2 text-left transition-colors hover:bg-sidebar-accent/50 ${
              isCollapsed ? 'lg:justify-center' : ''
            }`}
          >
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold ring-1 ring-zinc-200/70 dark:ring-zinc-700/50 ${
                user?.avatarUrl || user?.avatar_url ? 'bg-transparent' : 'bg-zinc-700 text-white'
              }`}
            >
              {user?.avatarUrl || user?.avatar_url ? (
                <img
                  src={user.avatarUrl || user.avatar_url}
                  alt={displayName}
                  className="h-full w-full bg-transparent object-contain"
                />
              ) : (
                displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
              )}
            </span>
            <span className={`min-w-0 flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>
              <strong className="block truncate text-xs text-sidebar-foreground">{displayName}</strong>
              <small className="block truncate text-[10px] text-sidebar-foreground/60">{user?.email || ''}</small>
            </span>
            <MoreVertical size={18} className={`shrink-0 text-sidebar-foreground/60 ${isCollapsed ? 'lg:hidden' : ''}`} />
          </button>

          {accountMenuOpen && (
            <section
              aria-label={t('Menuja e llogarisë')}
              className="absolute bottom-2 left-0 z-[60] w-full rounded-2xl border border-zinc-200/80 dark:border-white/15 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl backdrop-saturate-150 text-popover-foreground shadow-[0_20px_50px_rgba(0,0,0,0.18)] lg:left-full lg:ml-2 lg:w-[17rem] overflow-hidden animate-scaleIn"
            >
              <div className="flex items-center gap-3 rounded-t-xl bg-muted/20 px-3 py-3">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold ${
                    user?.avatarUrl || user?.avatar_url ? 'bg-transparent' : 'bg-zinc-700 text-white'
                  }`}
                >
                  {user?.avatarUrl || user?.avatar_url ? (
                    <img
                      src={user.avatarUrl || user.avatar_url}
                      alt=""
                      className="h-full w-full bg-transparent object-contain"
                    />
                  ) : (
                    displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
                  )}
                </span>
                <span className="min-w-0">
                  <strong className="block truncate text-sm">{displayName}</strong>
                  <small className="block truncate text-xs text-muted-foreground">{user?.email || ''}</small>
                </span>
              </div>

              {/* Language switcher */}
              <div
                className="relative"
                onMouseEnter={() => setLanguageMenuOpen(true)}
                onMouseLeave={() => setLanguageMenuOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setLanguageMenuOpen(true)}
                  onFocus={() => setLanguageMenuOpen(true)}
                  aria-label={t('Zgjidh gjuhën')}
                  aria-expanded={languageMenuOpen}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                    languageMenuOpen ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                  }`}
                >
                  <Globe2 size={17} className="text-muted-foreground" />
                  <span className="flex-1">{t('Gjuha')}</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </button>
                {languageMenuOpen && (
                  <div
                    aria-label={t('Zgjidh gjuhën')}
                    className="absolute bottom-full left-0 right-0 mb-1 z-[70] rounded-2xl border border-zinc-200/80 dark:border-white/15 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl p-1.5 text-popover-foreground shadow-2xl lg:bottom-auto lg:mb-0 lg:left-full lg:right-auto lg:top-0 lg:w-[12rem] overflow-hidden"
                  >
                    {[
                      { code: 'sq', flag: 'al', label: 'Shqip' },
                      { code: 'de', flag: 'de', label: 'Gjermanisht' },
                    ].map(({ code, flag, label }) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          setLanguage(code);
                          setLanguageMenuOpen(false);
                        }}
                        aria-pressed={language === code}
                        className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                          language === code ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                        }`}
                      >
                        <span className={`fi fi-${flag}`} aria-label={label} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Settings link */}
              <Link
                to="/settings"
                onClick={() => {
                  setAccountMenuOpen(false);
                  onClose();
                }}
                className="flex items-center gap-3 border-b border-zinc-200/70 px-4 py-2.5 text-sm transition-colors hover:bg-zinc-100 dark:border-zinc-700/50 dark:hover:bg-zinc-800"
              >
                <Settings size={17} className="text-muted-foreground" />
                {t('Cilësimet')}
              </Link>

              {/* Logout button */}
              <button
                type="button"
                onClick={() => {
                  setAccountMenuOpen(false);
                  logout();
                  onClose();
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <LogOut size={17} className="text-muted-foreground" />
                {t('Dil nga llogaria')}
              </button>
            </section>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
